import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Logo from '../components/Logo';
import { Seo } from '../components/Seo';
import { Badge } from '../components/ui/badge';
import { formatCurrency, formatDate, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/apiError';
import axios from 'axios';
import { FileText, CheckCircle, Loader2, CreditCard, Download, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicInvoice = () => {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Get font families based on branding
  const getFontStyles = () => {
    if (invoice?.template === 'brutalist') {
      return { heading: '"Space Grotesk", sans-serif', body: '"Courier Prime", monospace' };
    }
    if (!invoice?.branding?.font_pairing) return {};
    
    const fontMap = {
      classic: { heading: '"Inter", sans-serif', body: '"Inter", sans-serif' },
      editorial: { heading: '"Playfair Display", serif', body: '"Source Sans 3", sans-serif' },
      modern: { heading: '"Inter", sans-serif', body: '"Inter", sans-serif' },
      geometric: { heading: '"Space Grotesk", sans-serif', body: '"Inter", sans-serif' },
      literary: { heading: '"Lora", serif', body: '"Source Sans 3", sans-serif' },
      mono: { heading: '"Courier Prime", monospace', body: '"Courier Prime", monospace' }
    };
    
    return fontMap[invoice.branding.font_pairing] || fontMap.classic;
  };

  const fontStyles = invoice ? getFontStyles() : { heading: 'inherit', body: 'inherit' };
  const isBrutalist = invoice?.template === 'brutalist';
  const brandPrimary = invoice?.branding?.primary_color || '#233FE0';

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/invoice/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice?.invoice_number || 'invoice'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // Check for payment completion
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/invoice/${invoiceId}`);
      setInvoice(response.data);
    } catch (error) {
      toast.error('Invoice not found');
    } finally {
      setLoading(false);
    }
  };

  // Load Google Fonts based on branding font_pairing
  useEffect(() => {
    if (!invoice?.branding?.font_pairing) return;
    
    const fontPairingToGoogleFonts = {
      classic: 'Inter:wght@400;500;600;700',
      editorial: 'Playfair+Display:wght@400;700&family=Source+Sans+3:wght@400;600',
      modern: 'Inter:wght@400;500;600;700',
      geometric: 'Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600',
      literary: 'Lora:wght@400;600;700&family=Source+Sans+3:wght@400;600',
      mono: 'Courier+Prime:wght@400;700'
    };
    
    const fontUrl = invoice?.template === 'brutalist'
      ? 'Space+Grotesk:wght@700&family=Courier+Prime:wght@400;700'
      : fontPairingToGoogleFonts[invoice.branding.font_pairing];
    if (!fontUrl) return;
    
    // Check if already loaded
    const existingLink = document.querySelector(`link[href*="${fontUrl.split('&')[0]}"]`);
    if (existingLink) return;
    
    // Load Google Fonts
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${fontUrl}&display=swap`;
    document.head.appendChild(link);
    
    return () => {
      // Cleanup is optional as fonts stay loaded
    };
  }, [invoice?.branding?.font_pairing]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh the page.');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
      
      if (response.data.status === 'paid') {
        toast.success('Payment successful! Thank you.');
        setPaymentSuccess(true);
        fetchInvoice();
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error checking payment:', error);
    }
  };

  const handlePayNow = async () => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API_URL}/api/payments/invoice/${invoiceId}/checkout`, {
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Unable to start payment. Please try again or contact the sender.');
        setProcessingPayment(false);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to start payment'));
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Seo noindex />
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-bold mb-2">Invoice Not Found</h2>
            <p className="text-muted-foreground">This invoice does not exist or has been removed.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-invoice-page">
      <Seo noindex />
      {/* Header */}
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo variant="restrained" size="sm" wordmarkClassName="text-base" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Payment Success Banner */}
        {(paymentSuccess || invoice.status === 'paid') && (
          <Card className="mb-8 border-foreground/40 bg-muted/40">
            <CardContent className="p-6 flex items-center gap-4">
              <CheckCircle className="w-8 h-8 text-foreground" />
              <div>
                <h3 className="font-bold text-lg text-foreground">Payment Complete</h3>
                <p className="text-muted-foreground">
                  This invoice has been paid. Thank you!
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Updated Invoice Banner */}
        {invoice.version > 1 && (
          <Card className="mb-8 border-foreground/40 bg-muted/40">
            <CardContent className="p-6 flex items-center gap-4">
              <AlertTriangle className="w-8 h-8 text-foreground" />
              <div>
                <h3 className="font-bold text-lg text-foreground">
                  This Invoice Has Been <span style={{ color: '#96654A' }}>Updated</span>
                </h3>
                <p className="text-muted-foreground">
                  You are viewing version {invoice.version}. Please review the updated details below.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invoice Card */}
        <Card className="overflow-hidden" style={{ fontFamily: fontStyles.body, ...(isBrutalist ? { borderTop: `3px solid ${brandPrimary}`, borderRadius: 0 } : {}) }}>
          <CardHeader className="bg-muted/50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-2xl" style={{ fontFamily: fontStyles.heading }}>{invoice.invoice_number}</CardTitle>
                <CardDescription>
                  From {invoice.freelancer_name}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleDownloadPDF} data-testid="download-pdf-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Badge variant="secondary" className={`text-sm ${getStatusColor(invoice.status)}`}>
                  {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                </Badge>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6 space-y-8">
            {/* Branding Header */}
            {invoice.branding?.logo_base64 && (
              <div className="flex items-center gap-4 pb-6 border-b">
                <img 
                  src={invoice.branding.logo_base64} 
                  alt="Business Logo" 
                  className="max-w-[180px] max-h-[60px] w-auto h-auto object-contain"
                />
                {invoice.branding?.business_name && (
                  <div>
                    <p className="font-semibold text-lg">{invoice.branding.business_name}</p>
                    {invoice.branding?.business_website && (
                      <p className="text-sm text-muted-foreground">{invoice.branding.business_website}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Parties */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">From</p>
                <p className="font-medium">{invoice.branding?.business_name || invoice.freelancer_name}</p>
                <p className="text-muted-foreground">{invoice.freelancer_email}</p>
                {invoice.branding?.business_address && (
                  <p className="text-sm text-muted-foreground whitespace-pre-line mt-1">{invoice.branding.business_address}</p>
                )}
                {invoice.branding?.business_phone && (
                  <p className="text-sm text-muted-foreground">{invoice.branding.business_phone}</p>
                )}
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Bill To</p>
                <p className="font-medium">{invoice.client_name}</p>
                <p className="text-muted-foreground">{invoice.client_email}</p>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Issue Date</p>
                <p className="font-medium">{formatDate(invoice.created_at)}</p>
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">Due Date</p>
                <p className="font-medium">{formatDate(invoice.due_date)}</p>
              </div>
            </div>

            {/* Line Items */}
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">Items</p>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium">Description</th>
                      <th className="text-right p-3 text-sm font-medium">Qty</th>
                      <th className="text-right p-3 text-sm font-medium">Price</th>
                      <th className="text-right p-3 text-sm font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="p-3">{item.description}</td>
                        <td className="p-3 text-right font-mono">{item.quantity}</td>
                        <td className="p-3 text-right font-mono">{formatCurrency(item.unit_price)}</td>
                        <td className="p-3 text-right font-mono font-medium">
                          {formatCurrency(item.quantity * item.unit_price)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Total */}
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono">{formatCurrency(invoice.subtotal)}</span>
                </div>
                {invoice.tax_rate > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax ({invoice.tax_rate}%)</span>
                    <span className="font-mono">{formatCurrency(invoice.tax_amount || 0)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold border-t pt-2">
                  <span>Total</span>
                  <span className="font-mono">{formatCurrency(invoice.total)}</span>
                </div>
                {(invoice.amount_paid > 0 || invoice.status === 'partially_paid') && (
                  <>
                    <div className="flex justify-between text-sm text-emerald-600">
                      <span>Amount Paid</span>
                      <span className="font-mono">-{formatCurrency(invoice.amount_paid || 0)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold text-primary border-t pt-2">
                      <span>Balance Due</span>
                      <span className="font-mono">{formatCurrency(invoice.balance_due || invoice.total)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div className="pt-4 border-t">
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Notes</p>
                <p className="text-muted-foreground whitespace-pre-wrap">{invoice.notes}</p>
              </div>
            )}

            {/* Custom Invoice Footer */}
            {invoice.branding?.invoice_footer && (
              <div className="pt-4 text-center">
                <p className="text-sm text-muted-foreground">{invoice.branding.invoice_footer}</p>
              </div>
            )}

            {/* Pay Button - Only show if online payments are available */}
            {invoice.status !== 'paid' && invoice.status !== 'draft' && invoice.online_payments_available && (
              <div className="pt-6 border-t">
                <Button 
                  size="lg" 
                  className="w-full gap-2 bg-foreground text-background hover:bg-foreground/90 shadow-sm"
                  onClick={handlePayNow}
                  disabled={processingPayment}
                  data-testid="pay-now-btn"
                >
                  {processingPayment ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Pay {formatCurrency(invoice.balance_due || invoice.total)} Now
                    </>
                  )}
                </Button>
                <p className="text-center text-xs text-muted-foreground mt-2">
                  Secure payment powered by Stripe
                </p>
              </div>
            )}
            
            {/* Payment Instructions - Show when online payments NOT available */}
            {invoice.status !== 'paid' && invoice.status !== 'draft' && !invoice.online_payments_available && (
              <div className="pt-6 border-t">
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className="font-medium mb-2">Payment Instructions</p>
                  <p className="text-sm text-muted-foreground">
                    Please contact <span className="font-medium">{invoice.freelancer_name}</span> at{' '}
                    <a href={`mailto:${invoice.freelancer_email}`} className="text-primary hover:underline">
                      {invoice.freelancer_email}
                    </a>{' '}
                    for payment arrangements.
                  </p>
                  {invoice.notes && (
                    <p className="text-sm text-muted-foreground mt-2">
                      See the notes section above for additional payment details.
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Powered by branding - only shown for free tier */}
        {invoice.show_powered_by && (
          <p className="flex items-center justify-center gap-2 text-center text-xs text-muted-foreground mt-6">
            <span
              aria-hidden="true"
              className="inline-flex items-center justify-center w-5 h-5 rounded-[4px] bg-foreground text-background font-heading font-bold text-[9px] tracking-tight"
            >
              CC
            </span>
            <span>Powered by <span className="font-medium">CreateCollect</span></span>
          </p>
        )}
      </main>
    </div>
  );
};

export default PublicInvoice;
